import React, { useState } from 'react';
import {
  Button,
  Tooltip,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { FiPhone, FiPhoneCall } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';

// Utility to format phone for display
const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  const digitsOnly = phone.replace(/\D/g, '');
  
  // US format: (XXX) XXX-XXXX
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }
  
  // US with country code
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    const usNumber = digitsOnly.slice(1);
    return `+1 (${usNumber.slice(0, 3)}) ${usNumber.slice(3, 6)}-${usNumber.slice(6)}`;
  }
  
  return phone;
};

export default function DialpadCallButton({ 
  phoneNumber, 
  contactName,
  dealId = null,
  contactId = null,
  size = 'sm',
  isDisabled = false,
  label = null,
}) {
  const [isCalling, setIsCalling] = useState(false);
  const toast = useToast();

  // Validate phone number
  const hasValidPhone = phoneNumber && phoneNumber.replace(/\D/g, '').length >= 10;
  const formattedPhone = formatPhoneForDisplay(phoneNumber);

  const handleCall = async (e) => {
    e.stopPropagation();
    
    if (!hasValidPhone) {
      toast({
        title: 'Invalid phone number',
        description: 'Phone number must have at least 10 digits',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsCalling(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const initiateCall = httpsCallable(functions, 'initiateDialpadCall');
      
      const result = await initiateCall({
        phoneNumber,
        dealId,
        contactId,
      });

      if (result.data.success) {
        toast({
          title: 'Call initiated',
          description: `Calling ${contactName || formattedPhone} via Dialpad`,
          status: 'success',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      
      let errorMessage = error.message;
      if (error.message.includes('not enabled') || error.message.includes('failed-precondition')) {
        errorMessage = 'Dialpad is not enabled for your account. Please enable it in CRM Settings.';
      } else if (error.message.includes('Rate limit') || error.message.includes('resource-exhausted')) {
        errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
      } else if (error.message.includes('Invalid phone number')) {
        errorMessage = error.message; // Use the detailed phone validation error
      } else if (error.message.includes('authentication failed') || error.message.includes('permission-denied')) {
        errorMessage = 'Dialpad authentication failed. Please contact your administrator.';
      } else if (error.message.includes('Dialpad API error')) {
        errorMessage = error.message; // Pass through Dialpad API errors
      }
      
      toast({
        title: 'Could not initiate call',
        description: errorMessage,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsCalling(false);
    }
  };

  return (
    <Tooltip 
      label={hasValidPhone ? `Call ${formattedPhone} via Dialpad` : 'Invalid phone number'} 
      hasArrow
    >
      <Button
        leftIcon={<Icon as={FiPhoneCall} />}
        size={size}
        w="full"
        colorScheme="blue"
        onClick={handleCall}
        isLoading={isCalling}
        loadingText="Calling..."
        isDisabled={isDisabled || !hasValidPhone}
        variant="solid"
      >
        {label || 'Call (Dialpad)'}
      </Button>
    </Tooltip>
  );
}

